import { Injectable } from '@angular/core';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import esriConfig from '@arcgis/core/config';
import * as urlUtils from '@arcgis/core/core/urlUtils';

import { ConfigService } from '../../config.service';

import moment from "moment-timezone";
import { Utilities } from 'src/app/_helpers/utility/utilities';

export interface LegacyIncidentQueryParams {
  usePartialMatch?: boolean;
  irwinId?: string;
  incidentName?: string;
  //fireYear?: number;
  discoveredDateOn?: Date;
  discoveredDateMin?: Date;
  discoveredDateMax?: Date;
  //containedDate?: Date;
  //controlledDate?: Date;
  outDateOn?: Date;
  outDateMin?:Date;
  outDateMax?:Date;
  outDateNotOut?:boolean;
  geographicAreaName?: string,
  //AGENCY IS ALSO KNOWN AS JURISDICTIONAL AGENCY
  agency?: string;
  //RESPONSIBLE UNIT IS ALSO KNOWN AS PROTECTING UNIT
  responsibleUnits?: string[],
  incidentSizeAcresMin?:number,
  incidentSizeAcresMax?:number,
  estimatedPublishedFinalCostMin?:number,
  estimatedPublishedFinalCostMax?:number,
  state?:string,
  jurisdictionalUnits?:string[],
  hasPublishedDecision?:boolean,
  hasCompletedAnalysis?:boolean,

}
export interface LegacyIncident{
  incidentName?: string;
  uniqueFireId?: string;
  pCode?: string;
  fireCode?: string;
  irwinId?: string;
  discoveredDate?: string;
  containedDate?: string;
  controlledDate?: string;
  outDate?: string;
  fireYear?: number;
  localNum?: string;
  latitude?: number;
  longitude?: number;
  responsibleUnit?: string;
  responsibleUnitName?: string;
  jurisdictionalUnit?: string;
  jurisdictions?: number;
  jurisdictionList?: string;
  wfdssId?: string;
  acreage?: number;
  dispatchId?: string;
  estimatedPublishedFinalCost?: number;
  estimatedCost?: number;
  incidentType?: string;
  incidentTypeCategory?: string;
  incidentTypeKind?: string;
  state?: string;
  timezone?: string;
  isValid?: string;
  cause?: string;
  creationDate?: string;
  latestPublishedDecision?: string;
  geographicAreaName?: string;
  agency?: string;
  hasPublishedRelativeRisk?: number;
  hasPublishedOrgAssessment?: number;
  countAnalysisReview?: number;
  countAnalysisComplete?: number;
  numPublishedDecisions?: number;
  latestWfdssPerimiterSize?: number;
  latestHistoryTime?: string;
  latestAnalysisTime?: string;
  xCoord?: number,
  yCoord: number;
}

@Injectable({
  providedIn: 'root'
})
export class LegacyIncidentService {

  proxyUrl:string = '';
  wfdssLegacyIncidentLayer:FeatureLayer;

  constructor(private environment: ConfigService){
    
    if (this.environment.name.toLowerCase() === "prd") {
      this.proxyUrl = "https://wfdss.firenet.gov/resourceproxy/proxy";
    } else {
      this.proxyUrl = `https://wfdss${this.environment.name}.firenet.gov/resourceproxy/proxy`;
    }
    //esriConfig.request.proxyUrl = this.proxyUrl;
    
    this.wfdssLegacyIncidentLayer = new FeatureLayer({
      url: `${environment.arcGisServerHost}/arcgis/rest/services/WFDSS/WfdssLegacyIncidents_${environment.name}/FeatureServer/0`,
      outFields: ['*']
    });

    urlUtils.addProxyRule({
      urlPrefix: this.wfdssLegacyIncidentLayer.url,
      proxyUrl: this.proxyUrl,
    });
  }
  
  private convertLegacyIncidentWhereParamsToWhereClause(whereParams:LegacyIncidentQueryParams):string{
    const whereArray = [];

    if(whereParams.irwinId){
      whereArray.push(this.convertStringValueToStringQuery('IRWIN_GUID', whereParams.irwinId, whereParams.usePartialMatch));
    }
    if(whereParams.incidentName){
      whereArray.push(this.convertStringValueToStringQuery('INCIDENT_NAME', whereParams.incidentName, whereParams.usePartialMatch));
    }
    // if(whereParams.fireYear){
    //   whereArray.push(this.convertStringValueToStringQuery('FIRE_YEAR', whereParams.fireYear.toString(), false));
    // }
    //ALSO KNOWN AS PROTECTING UNIT
    if(whereParams.responsibleUnits && whereParams.responsibleUnits.length > 0){
      whereArray.push(this.convertStringArrayToStringQuery('RESPONSIBLE_UNIT', whereParams.responsibleUnits));
    }
    //ALOS KNOWN AS JURISDICTIONAL AGENCY
    if(whereParams.agency){
      whereArray.push(this.convertStringValueToStringQuery('AGENCY', whereParams.agency, false));
    }
    if(whereParams.geographicAreaName){
      whereArray.push(this.convertStringValueToStringQuery('GEOGRAPHIC_AREA_NAME', whereParams.geographicAreaName, false));
    }
    if(whereParams.discoveredDateOn){
      whereArray.push(this.convertDateToTimestampDateQuery('DISCOVER_DATE', whereParams.discoveredDateOn));
    }
    if(whereParams.discoveredDateMin && whereParams.discoveredDateMax){
      whereArray.push(this.convertDateToTimestampDateMinQuery('DISCOVER_DATE', whereParams.discoveredDateMin, true));
      whereArray.push(this.convertDateToTimestampDateMaxQuery('DISCOVER_DATE', whereParams.discoveredDateMax, true));
    }  else {
      if(whereParams.discoveredDateMin){
        whereArray.push(this.convertDateToTimestampDateMinQuery('DISCOVER_DATE', whereParams.discoveredDateMin, false));
      }
      if(whereParams.discoveredDateMax){
        whereArray.push(this.convertDateToTimestampDateMaxQuery('DISCOVER_DATE', whereParams.discoveredDateMax, false));
      }
    }
    // if(whereParams.containedDate){
    //     whereArray.push(this.convertDateToTimestampDateQuery('CONTAINED_DATE', whereParams.containedDate));
    // }
    // if(whereParams.controlledDate){
    //   whereArray.push(this.convertDateToTimestampDateQuery('CONTROLLED_DATE', whereParams.controlledDate));
    // }
    if(whereParams.outDateOn){
        whereArray.push(this.convertDateToTimestampDateQuery('OUT_DATE', whereParams.outDateOn));
    }
    if(whereParams.outDateMin && whereParams.outDateMax){
        whereArray.push(this.convertDateToTimestampDateMinQuery('OUT_DATE', whereParams.outDateMin, true));
        whereArray.push(this.convertDateToTimestampDateMaxQuery('OUT_DATE', whereParams.outDateMax, true));
    } else {
      if(whereParams.outDateMin){
        whereArray.push(this.convertDateToTimestampDateMinQuery('OUT_DATE', whereParams.outDateMin, false));
      }
      if(whereParams.outDateMax){
          whereArray.push(this.convertDateToTimestampDateMaxQuery('OUT_DATE', whereParams.outDateMax, false));
      }
    }
    if(whereParams.outDateNotOut == true){
      whereArray.push('OUT_DATE IS NULL');
    }
    if(whereParams.incidentSizeAcresMin != null){
        whereArray.push(`(ACREAGE >= ${whereParams.incidentSizeAcresMin})`);
    }
    if(whereParams.incidentSizeAcresMax != null){
        whereArray.push(`(ACREAGE <= ${whereParams.incidentSizeAcresMax})`);
    }
    if(whereParams.estimatedPublishedFinalCostMin != null){
        whereArray.push(`(ESTIMATED_PUBLISHED_FINAL_COST >= ${whereParams.estimatedPublishedFinalCostMin})`);
    }
    if(whereParams.estimatedPublishedFinalCostMax != null){
        whereArray.push(`(ESTIMATED_PUBLISHED_FINAL_COST <= ${whereParams.estimatedPublishedFinalCostMax})`);
    }
    if(whereParams.state){
      whereArray.push(this.convertStringValueToStringQuery('STATE_ABBREV', whereParams.state, false));
    }
    if(whereParams.jurisdictionalUnits && whereParams.jurisdictionalUnits.length > 0){
      whereArray.push(this.convertStringArrayToStringQuery('JURISDICTIONAL_UNIT', whereParams.jurisdictionalUnits));
    }
    if(whereParams.hasPublishedDecision != null){
      if(whereParams.hasPublishedDecision){
        whereArray.push('(NUM_PUBLISHED_DECISIONS > 0)')
      } else {
        whereArray.push('(NUM_PUBLISHED_DECISIONS = 0)')
      }
    }
    if(whereParams.hasCompletedAnalysis != null){
      if(whereParams.hasCompletedAnalysis){
        whereArray.push('(COUNT_ANALYSIS_COMPLETE > 0)')
      } else {
        whereArray.push('(COUNT_ANALYSIS_COMPLETE = 0)')
      }
    }
    
    return whereArray.join(' AND ');
  }

  private convertStringValueToStringQuery(fieldName:string, value:string, usePartialMatch?:boolean){
    const valueUpper = value.toUpperCase();
    const stringQuery = usePartialMatch ? `(UPPER(${fieldName}) LIKE '%${valueUpper}%')` : `(UPPER(${fieldName}) = '${valueUpper}')`;
    return stringQuery;
  }

  private convertStringArrayToStringQuery(fieldName:string, values:string[]){
    const stringQueries = values.map(v => { return `UPPER(${fieldName}) = '${v.toUpperCase()}'`});
    const stringQuery = `(${stringQueries.join(' OR ')})`
    return stringQuery;
  }

  private convertDateToTimestampDateQuery(fieldName:string, date:Date):string {
    const day:string = moment(date).tz('UTC').format('YYYY-MM-DD');
    const beginningOfDay:string = '00:00:00';
    const endOfDay:string = '23:59:59';
    const timestampRangeQuery = `(${fieldName} >= timestamp'${day} ${beginningOfDay}' AND ${fieldName} <= timestamp'${day} ${endOfDay}')`;
    return timestampRangeQuery;
  }

  private convertDateToTimestampDateMaxQuery(fieldName:string, date:Date, inclusiveDate?:boolean):string {
    const momentDate = moment(date).tz('UTC')
    if(inclusiveDate){ // For 'between' date queries, we want to include incidents discovered ON the start date and ON end date
      momentDate.add(1, "day")
    }
    const day:string = momentDate.format('YYYY-MM-DD');
    const beginningOfDay:string = '00:00:00';
    const timestampRangeQuery = `(${fieldName} < timestamp'${day} ${beginningOfDay}')`;
    return timestampRangeQuery;
  }

  private convertDateToTimestampDateMinQuery(fieldName:string, date:Date, inclusiveDate?:boolean):string {
    const momentDate = moment(date).tz('UTC')
    if(inclusiveDate){ // For 'between' date queries, we want to include incidents discovered ON the start date and ON end date
      momentDate.subtract(1, "day")
    }
    const day:string = momentDate.format('YYYY-MM-DD');
    const endOfDay:string = '23:59:59';
    const timestampRangeQuery = `(${fieldName} > timestamp'${day} ${endOfDay}')`;
    return timestampRangeQuery;
  }

  
  private legacyIncidentFeatureAttributesToLegacyIncident(feature):LegacyIncident{
    const attributes = feature.attributes;
    const geometry = feature.geometry;
    return {
      incidentName: attributes['incident_name'],
      uniqueFireId: attributes['unique_fire_id'],
      pCode: attributes['p_code'],
      fireCode: attributes['fire_code'],
      irwinId: attributes['irwin_guid'],
      discoveredDate: attributes['discover_date'],
      containedDate: attributes['contained_date'],
      controlledDate: attributes['controlled_date'],
      outDate: attributes['out_date'],
      fireYear: attributes['fire_year'],
      localNum: attributes['local_num'],
      latitude: attributes['latitude'],
      longitude: attributes['longitude'],
      responsibleUnit: attributes['responsible_unit'],
      responsibleUnitName: attributes['responsible_unit_name'],
      jurisdictionalUnit: attributes['jurisdictional_unit'],
      jurisdictions: attributes['jurisdictions'],
      jurisdictionList: attributes['jurisdiction_list'],
      wfdssId: attributes['wfdss_id'],
      acreage: Utilities.truncateDecimalPlaces(attributes['acreage']),
      dispatchId: attributes['dispatch_id'],
      estimatedPublishedFinalCost: attributes['estimated_published_final_cost'],
      estimatedCost: attributes['estimated_cost'],
      incidentType: attributes['type'],
      incidentTypeCategory: attributes['incident_type_category'],
      incidentTypeKind: attributes['incident_type_kind'],
      state: attributes['state_abbrev'],
      timezone: attributes['timezone'],
      isValid: attributes['is_valid'],
      cause: attributes['cause'],
      creationDate: attributes['creation_date'],
      latestPublishedDecision: attributes['latest_published_decision'],
      geographicAreaName: attributes['geographic_area_name'],
      agency: attributes['agency'],
      hasPublishedRelativeRisk: attributes['has_published_relative_risk'],
      hasPublishedOrgAssessment: attributes['has_published_org_assessment'],
      countAnalysisReview: attributes['count_analysis_review'],
      countAnalysisComplete: attributes['count_analysis_complete'],
      numPublishedDecisions: attributes['num_published_decisions'],
      latestWfdssPerimiterSize: attributes['latest_wfdss_perimeter_size'],
      latestHistoryTime: attributes['latest_history_time'],
      latestAnalysisTime: attributes['latest_analysis_time'],
      xCoord: geometry?.x,
      yCoord: geometry?.y
    }
  }

  queryLegacyIncidents(whereParams:LegacyIncidentQueryParams):Promise<LegacyIncident[]>{
    const whereClause = this.convertLegacyIncidentWhereParamsToWhereClause(whereParams);
    const queryParams = {
      where: whereClause,
      outFields: ['*'],
      returnGeometry: true,
    }

    //return this.esriMapService.wfdssLegacyIncidentLayer.queryFeatures(queryParams)
    return this.wfdssLegacyIncidentLayer.queryFeatures(queryParams)
    .then(featureSet => {
      const legacyIncidents = featureSet.features.map((f)=>{
        return this.legacyIncidentFeatureAttributesToLegacyIncident(f);
      });
      return legacyIncidents;
    });
  }

}